<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import { ref } from "vue";
import Loading from 'vue-loading-overlay';
import { Link, useForm } from '@inertiajs/vue3';
import { useToast } from 'vue-toast-notification';

const props = defineProps({
    year: String,
    userById: Object,
    flash: Object,
    user: Object,
    userlogs: Object,
});

let isLoading = ref(false);
const $toast = useToast();

const PasswordUpdateform = useForm({
    firstpassword: '',
    password: '',
    userid: props.userById.id
});

const PasswordUpdateformSubmit = () => {
    PasswordUpdateform.post('/users/changePassword', {
        preserveScroll: true,
        onStart: () => isLoading.value = true,
        onFinish: () => isLoading.value = false,
        onSuccess: () => {
            $toast.open({
                message: props.flash.message,
                type: "info",
                duration: 4000,
                position: 'top-right',
                class: 'bs-toast toast bg-dark show'
            });
        }
    });
};

</script>
<style scoped>
@import "../../../../public/ui/vendor/libs/toastr/toastr.css";
@import "vue-loading-overlay/dist/vue-loading.css";
</style>
<template>
    <AppLayout title="Booking Details" :year=year :user=user>
        <div class="vld-parent">
            <Loading v-model:active="isLoading" :color="'#696cff'" />
        </div>
        <div class="container-xxl flex-grow-1 container-p-y">
            <h5 class="card-title fw-bold py-3 mb-4">User Details</h5>
            <div class="row">
                <!-- User Sidebar -->
                <div class="col-xl-4 col-lg-5 col-md-5 order-1 order-md-0">
                    <!-- User Card -->
                    <div class="card mb-4">
                        <div class="card-body">
                            <div class="user-avatar-section">
                                <div class="d-flex align-items-center flex-column">
                                    <img class="img-fluid rounded my-4" :src="userById.profile_photo_url" height="110"
                                        width="110" alt="User avatar" />

                                    <div class="user-info text-center">
                                        <h4 class="mb-2">{{ userById.name }}</h4>
                                        <span class="badge bg-label-secondary">{{ userById.email }}</span>
                                    </div>
                                </div>
                            </div>
                            <h5 class="pb-2 border-bottom mb-4">Details</h5>
                            <div class="info-container">
                                <ul class="list-unstyled">
                                    <li class="mb-3">
                                        <span class="fw-bold me-2">Full Name:</span>
                                        <span>{{ userById.name }}</span>
                                    </li>
                                    <li class="mb-3">
                                        <span class="fw-bold me-2">Email:</span>
                                        <span>{{ userById.email }}</span>
                                    </li>
                                    <li class="mb-3">
                                        <span class="fw-bold me-2">Status:</span>
                                        <span v-if="userById.active == 1" class="badge bg-label-success">Active</span>
                                        <span v-else class="badge bg-label-danger">Inactive</span>
                                    </li>
                                    <li class="mb-3">
                                        <span class="fw-bold me-2">Parent:</span>
                                        <span v-if="userById.user_type == 'Company'">{{ userById.company.name }}</span>
                                        <span v-else>{{ userById.agent.agency_name }}</span>
                                    </li>
                                </ul>
                                <div class="d-flex justify-content-center pt-3">
                                    <a href="javascript:;" class="btn btn-primary me-3" data-bs-target="#editUser"
                                        data-bs-toggle="modal">Edit</a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-xl-8 col-lg-7 col-md-7 order-0 order-md-1">
                    <!-- User Pills -->
                    <ul class="nav nav-pills flex-column flex-md-row mb-3">
                        <li class="nav-item">
                            <Link class="nav-link" :href="'/users/show/' + userById.id"><i
                                class="bx bx-user me-1"></i>Activity
                            </Link>
                        </li>
                        <li class="nav-item">
                            <Link class="nav-link active" :href="'/users/securityTab/' + userById.id"
                                v-if="$page.props.user.permissions.includes('view-security-tab')"><i
                                class="bx bx-lock-alt me-1"></i>Security
                            </Link>
                        </li>
                        <li class="nav-item">
                            <Link class="nav-link" :href="'/users/permissionsTab/' + userById.id"
                                v-if="$page.props.user.permissions.includes('view-permissions-tab')"><i
                                class="bx bxs-user-badge me-1"></i>Permissions
                            </Link>
                        </li>
                    </ul>

                    <!-- Change Password -->
                    <div class="card mb-4">
                        <h5 class="card-header">Change Password</h5>
                        <div class="card-body">
                            <form @submit.prevent="PasswordUpdateformSubmit">
                                <div class="alert alert-warning" role="alert">
                                    <h6 class="alert-heading fw-bold mb-1">Ensure that these requirements are met</h6>
                                    <span>Minimum 8 characters long, uppercase, lowercase & symbol</span>
                                </div>
                                <div class="row">
                                    <div class="mb-3 col-12 col-sm-6 form-password-toggle">
                                        <label class="form-label" for="newPassword">New Password</label>
                                        <div class="input-group input-group-merge">
                                            <input class="form-control" type="password" name="firstpassword"
                                                v-model="PasswordUpdateform.firstpassword"
                                                placeholder="&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;"
                                                required="required" />
                                            <span class="input-group-text cursor-pointer"><i
                                                    class="bx bx-hide"></i></span>
                                        </div>
                                    </div>

                                    <div class="mb-3 col-12 col-sm-6 form-password-toggle">
                                        <label class="form-label" for="confirmPassword">Confirm New Password</label>
                                        <div class="input-group input-group-merge">
                                            <input class="form-control" type="password" name="password"
                                                v-model="PasswordUpdateform.password"
                                                placeholder="&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;"
                                                required="required"
                                                :class="{ 'is-invalid': PasswordUpdateform.errors.password }" />

                                            <span class="input-group-text cursor-pointer"><i
                                                    class="bx bx-hide"></i></span>
                                            <div v-if="PasswordUpdateform.errors.password" class="invalid-feedback">
                                                <div v-text="PasswordUpdateform.errors.password">
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <button type="submit" class="btn btn-primary me-2">Change Password</button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                    <!--/ Change Password -->
                    <!-- /Activity Timeline -->
                </div>
            </div>
        </div>
    </AppLayout>
</template>